import MuiPhoneNumber, { MuiPhoneNumberProps } from "mui-phone-number";
import {
  InputAdornment,
  IconButton,
  Box,
  Typography,
  Fade,
} from "@mui/material";
import { Copy } from "lucide-react";
import { useState } from "react";
import { formatInternationalPhoneNumber } from "common/utils";

type MyProps = MuiPhoneNumberProps & {
  onChange: (value: string) => void;
  disableDropdown?: boolean;
  handleCopy?: boolean; // New prop for enabling copy functionality
};

export default ({
  onChange,
  disableDropdown = true,
  handleCopy,
  sx,
  ...otherProps
}: MyProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopyClick = async () => {
    if (otherProps.value) {
      const textToCopy = formatInternationalPhoneNumber(
        otherProps.value as string,
        true,
      );
      if (textToCopy) {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500); // Show "Copied" for 1.5 seconds
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    // Get the selection range
    const input = e.target as HTMLInputElement;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    // If text is selected, let the default handler take care of it
    const isTextSelected = start !== end;
    if (isTextSelected) {
      return;
    }
    const pastedText = e.clipboardData.getData("text");
    const digitsOnly = pastedText.replace(/\D/g, "");

    // If it starts with +1, prevent MUI from adding another 1
    if (pastedText.startsWith("+1")) {
      e.preventDefault();
      onChange(pastedText);
      return;
    }

    // If it starts with 1 and has more digits, it's a US number
    if (pastedText.startsWith("1") && digitsOnly.length > 1) {
      e.preventDefault();
      onChange(`+${pastedText}`);
      return;
    }
    // Let MUI handle all other cases
  };

  return (
    <MuiPhoneNumber
      {...otherProps}
      sx={{
        ...sx,
        // Only relevant to international phone numbers
        ".MuiIconButton-root": {
          margin: "14px 0 0 14px !important",
          minWidth: "20px !important",
          height: "22px !important",
        },
        ".MuiOutlinedInput-input": {
          padding: disableDropdown ? undefined : 0,
        },
      }}
      variant="outlined"
      disableDropdown={disableDropdown}
      defaultCountry="us"
      preferredCountries={["us"]}
      autoFormat={true}
      disableAreaCodes={true}
      onChange={(e) => {
        onChange(typeof e === "string" ? e : e.target.value);
      }}
      onPaste={handlePaste}
      placeholder="Enter phone number"
      inputProps={{
        autoComplete: otherProps.autoComplete,
      }}
      InputProps={{
        endAdornment: handleCopy && otherProps.value && (
          <InputAdornment position="end" sx={{}}>
            <Box position="relative" display="flex" alignItems="center">
              <Fade in={!copied} timeout={300}>
                <IconButton
                  onClick={handleCopyClick}
                  edge="end"
                  sx={{
                    // Padding at the bottom is to counteract the !important marginTop we don't want.
                    paddingBottom: "14px",
                    // marginTop: "0px !important",
                  }}
                >
                  <Copy width="20" />
                </IconButton>
              </Fade>
              <Fade in={copied} timeout={300}>
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "700",
                    marginRight: "12px",
                  }}
                >
                  Copied
                </Typography>
              </Fade>
            </Box>
          </InputAdornment>
        ),
      }}
    />
  );
};
